/**
 * Cast Overlay Component
 *
 * Shown instead of the frozen local frame while a Chromecast session is
 * active ("Casting to <device>"). Fully self-contained:
 * it watches the controller's data-casting attribute (the single casting
 * state signal, maintained by bunny-stream-video and also driving the
 * casting CSS rules) and reads the device name from the cast SDK itself.
 */

import { t } from 'media-chrome/utils/i18n.js';
import { getCastDeviceName } from '../utils/cast-sender.ts';

const MESSAGE_KEY = 'Casting to {device}';

export class BunnyCastOverlay extends HTMLElement {
  private messageEl: HTMLElement | null = null;
  private controllerObserver: MutationObserver | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();

    const controller = this.closest('media-controller');
    if (controller) {
      this.controllerObserver = new MutationObserver(() =>
        this.updateMessage()
      );
      this.controllerObserver.observe(controller, {
        attributes: true,
        attributeFilter: ['data-casting'],
      });
    }
    this.updateMessage();
  }

  disconnectedCallback() {
    this.controllerObserver?.disconnect();
    this.controllerObserver = null;
  }

  private render() {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
            <style>
                :host {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: none;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 24px;
                    color: white;
                    background: linear-gradient(135deg, #1e2947 0%, #141c33 100%);
                    z-index: 2;
                    pointer-events: none;
                    padding: 16px;
                    box-sizing: border-box;
                    font-family: inherit;
                }
                .message {
                    text-align: center;
                    font-size: 1.25em;
                    font-weight: 400;
                    line-height: 1.3;
                }
            </style>
            <svg width="110" height="90" viewBox="0 0 24 20" aria-hidden="true">
                <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stop-color="#FFAF48" />
                        <stop offset="1" stop-color="#FF7854" />
                    </linearGradient>
                </defs>
                <path fill="url(#gradient)" d="M21 0H3C1.9 0 1 .9 1 2v3h2V2h18v14h-7v2h7c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2zM1 15v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2C12 12.92 7.07 7 1 7z" />
            </svg>
            <div class="message"></div>
        `;

    this.messageEl = this.shadowRoot.querySelector('.message');
  }

  /**
   * Localized at display time; the <strong> device name keeps its emphasis
   * wherever the language's word order places the {device} placeholder.
   */
  private updateMessage() {
    if (!this.messageEl) return;

    // Our key is not part of media-chrome's built-in translation key union;
    // t() resolves it through the registered Bunny dictionaries and falls
    // back to the key text itself.
    const template = t(MESSAGE_KEY as unknown as Parameters<typeof t>[0]);
    const [before, after = ''] = template.split('{device}');

    const deviceEl = document.createElement('strong');
    deviceEl.textContent = getCastDeviceName() ?? 'Chromecast';

    this.messageEl.replaceChildren(
      document.createTextNode(before),
      deviceEl,
      document.createTextNode(after),
    );
  }
}

if (!globalThis.customElements?.get('bunny-cast-overlay')) {
  globalThis.customElements?.define('bunny-cast-overlay', BunnyCastOverlay);
}
